import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Package, Plus, CheckCircle, Clock, XCircle, Eye, Trash2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3000';

export default function StockOpnamePage() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [notes, setNotes] = useState('');
  const [creating, setCreating] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Get current user role
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.role === 'admin';

  useEffect(() => {
    fetchSessions();
    fetchBranches();
  }, [filterStatus]);

  const fetchBranches = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/branches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBranches(response.data.branches || []);
    } catch (error) {
      console.error('Error fetching branches:', error);
      setBranches([]);
    }
  };

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = filterStatus !== 'all' ? { status: filterStatus } : {};
      
      const response = await axios.get(`${API}/api/stock-opname`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      
      setSessions(response.data.sessions || []);
    } catch (error) {
      console.error('Error fetching sessions:', error);
      toast.error('Gagal memuat data stock opname');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSession = async () => {
    if (!selectedBranch) {
      toast.error('Pilih branch terlebih dahulu');
      return;
    }

    setCreating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/stock-opname/create`,
        {
          branch_id: selectedBranch,
          opname_type: 'full',
          notes: notes
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        toast.success('Stock opname session berhasil dibuat!');
        setShowCreateModal(false);
        setSelectedBranch('');
        setNotes('');
        fetchSessions();
      }
    } catch (error) {
      console.error('Error creating session:', error);
      toast.error(error.response?.data?.detail || 'Gagal membuat session');
    } finally {
      setCreating(false);
    }
  };

  const handleStartSession = async (opnameId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/stock-opname/${opnameId}/start`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        toast.success(`${response.data.total_items} items berhasil dimuat!`);
        navigate(`/stock-opname/${opnameId}`);
      }
    } catch (error) {
      console.error('Error starting session:', error);
      toast.error(error.response?.data?.detail || 'Gagal memulai session');
    }
  };

  const handleDeleteSession = async (opnameId, sessionStatus) => {
    // Confirm delete
    const confirmMsg = sessionStatus === 'draft' 
      ? 'Yakin ingin membatalkan stock opname ini?' 
      : 'Yakin ingin menghapus stock opname ini? Data penghitungan akan hilang!';
    
    if (!window.confirm(confirmMsg)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/stock-opname/${opnameId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Stock opname berhasil dihapus');
      fetchSessions();
    } catch (error) {
      console.error('Error deleting session:', error);
      toast.error(error.response?.data?.detail || 'Gagal menghapus session');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { color: 'bg-gray-500', icon: Clock, text: 'Draft' },
      in_progress: { color: 'bg-blue-500', icon: Clock, text: 'Sedang Berjalan' },
      completed: { color: 'bg-green-500', icon: CheckCircle, text: 'Selesai' },
      cancelled: { color: 'bg-red-500', icon: XCircle, text: 'Dibatalkan' }
    };

    const config = statusConfig[status] || statusConfig.draft;
    const Icon = config.icon;

    return (
      <span className={`${config.color} text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {config.text}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen billing-admin-shell p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => navigate('/admin')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Kembali ke Dashboard
            </button>
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-purple-500 p-3 rounded-lg">
                <Package className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-800">Stock Opname</h1>
                <p className="text-gray-600">Kelola penghitungan stok fisik inventory</p>
              </div>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-purple-500 hover:bg-purple-600 text-white px-6 py-3 rounded-lg flex items-center gap-2 transition"
            >
              <Plus className="h-5 w-5" />
              Buat Stock Opname Baru
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-lg p-4 mb-6">
          <div className="flex gap-2">
            {['all', 'draft', 'in_progress', 'completed'].map((status) => (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                className={`px-4 py-2 rounded-lg transition ${
                  filterStatus === status
                    ? 'bg-purple-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {status === 'all' ? 'Semua' : status === 'draft' ? 'Draft' : status === 'in_progress' ? 'Berjalan' : 'Selesai'}
              </button>
            ))}
          </div>
        </div>

        {/* Sessions List */}
        <div className="space-y-4">
          {loading ? (
            <div className="bg-white rounded-lg shadow-lg p-12 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent mx-auto mb-4"></div>
              <p className="text-gray-600">Memuat data...</p>
            </div>
          ) : sessions.length === 0 ? (
            <div className="bg-white rounded-lg shadow-lg p-12 text-center">
              <Package className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600">Belum ada stock opname</p>
            </div>
          ) : (
            sessions.map((session) => (
              <div key={session.id} className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      {getStatusBadge(session.status)}
                      <h3 className="text-xl font-bold text-gray-800">{session.branch_name}</h3>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                      <div>
                        <p className="text-xs text-gray-500">Dibuat</p>
                        <p className="text-sm font-medium">{formatDate(session.created_at)}</p>
                        <p className="text-xs text-gray-600">{session.created_by_name}</p>
                      </div>
                      
                      {session.started_at && (
                        <div>
                          <p className="text-xs text-gray-500">Dimulai</p>
                          <p className="text-sm font-medium">{formatDate(session.started_at)}</p>
                        </div>
                      )}
                      
                      {session.completed_at && (
                        <div>
                          <p className="text-xs text-gray-500">Selesai</p>
                          <p className="text-sm font-medium">{formatDate(session.completed_at)}</p>
                          <p className="text-xs text-gray-600">Oleh: {session.approved_by_name}</p>
                        </div>
                      )}
                      
                      {session.summary && (
                        <div>
                          <p className="text-xs text-gray-500">Progress</p>
                          <p className="text-sm font-medium">
                            {session.summary.items_counted} / {session.summary.total_items} items
                          </p>
                          {session.summary.items_with_variance > 0 && (
                            <p className="text-xs text-orange-600">
                              {session.summary.items_with_variance} selisih
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    {session.notes && (
                      <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-700">{session.notes}</p>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-2 ml-4">
                    {session.status === 'draft' && (
                      <>
                        <button
                          onClick={() => handleStartSession(session.id)}
                          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2 transition"
                        >
                          <Clock className="h-4 w-4" />
                          Mulai
                        </button>
                        {isAdmin && (
                          <button
                            onClick={() => handleDeleteSession(session.id, 'draft')}
                            className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2 transition"
                          >
                            <Trash2 className="h-4 w-4" />
                            Hapus
                          </button>
                        )}
                      </>
                    )}
                    
                    {session.status === 'in_progress' && (
                      <>
                        <button
                          onClick={() => navigate(`/stock-opname/${session.id}`)}
                          className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2 transition"
                        >
                          <Eye className="h-4 w-4" />
                          Lanjut Hitung
                        </button>
                        {isAdmin && (
                          <button
                            onClick={() => handleDeleteSession(session.id, 'in_progress')}
                            className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2 transition"
                          >
                            <Trash2 className="h-4 w-4" />
                            Hapus
                          </button>
                        )}
                      </>
                    )}
                    
                    {session.status === 'completed' && (
                      <>
                        <button
                          onClick={() => navigate(`/stock-opname/${session.id}`)}
                          className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2 transition"
                        >
                          <Eye className="h-4 w-4" />
                          Lihat Detail
                        </button>
                        <button
                          onClick={() => {
                            navigate(`/stock-opname/${session.id}`);
                            setTimeout(() => window.print(), 1000);
                          }}
                          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2 transition"
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                          </svg>
                          Print
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Buat Stock Opname Baru</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Branch
                </label>
                <select
                  value={selectedBranch}
                  onChange={(e) => setSelectedBranch(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">Pilih Branch</option>
                  {branches.map((branch) => (
                    <option key={branch.branch_id} value={branch.branch_id}>
                      {branch.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Catatan (Optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Misal: Stock opname bulan Januari 2025"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  rows="3"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-lg transition"
                disabled={creating}
              >
                Batal
              </button>
              <button
                onClick={handleCreateSession}
                disabled={creating || !selectedBranch}
                className="flex-1 bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-lg transition disabled:opacity-50"
              >
                {creating ? 'Membuat...' : 'Buat Session'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
